import React from 'react';
import FlowDiagram from '../components/marketing/sections/FlowDiagram';
import FinalCTASection from '../components/marketing/sections/FinalCTASection';
import ExpandableCard from '../components/marketing/ExpandableCard';
import styles from './MarketingPage.module.css';
import sectionStyles from '../components/marketing/sections/sections.module.css';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useReveal from '../hooks/useReveal';
import { CheckIcon } from '../components/marketing/MarketingIcons';

const DETAIL_SECTIONS = [
  {
    heading: 'Sharing your details',
    body: 'Your Velox profile opens on the other person\u2019s phone from a contactless card, or from a link. There is nothing for them to download, and nothing for them to type. If their phone does not support a tap, the same profile is reachable by scanning or by link, so you are never stuck.',
    points: [
      'Works on the recipient\u2019s browser, with no app install',
      'Your profile shows your current role, company and links',
      'Update your details once and every share is current',
    ],
  },
  {
    heading: 'Capturing the other person',
    body: 'Sharing your details is only half of an introduction. From your profile, the person you have just met can send their own details straight back to you. They do not need a Velox account to do it, which is what stops the majority of introductions from being lost.',
    points: [
      'Guest capture for people with no Velox account',
      'Contacts arrive in your list immediately',
      'Contacts you no longer need can be archived rather than deleted',
    ],
  },
  {
    heading: 'Keeping the context',
    body: 'A name and a company are rarely enough to act on a fortnight later. Velox records where and when each contact was made, and gives you a private space to write down what you agreed, the detail that actually makes a follow-up land. When they update their details, your copy updates too.',
    points: [
      'The location and date of the meeting are stored with the contact',
      'Private notes are visible only to you, including in company accounts',
      'Contacts can be viewed on a map to jog your memory of an event',
    ],
  },
  {
    heading: 'Finding them again',
    body: 'Networking activity is usually invisible, which makes it impossible to improve or to justify. Velox shows you what happened after the meeting: who looked at your profile, which links they opened, and how often your card is being used, and it keeps the person findable when they matter.',
    points: [
      'Profile views and link clicks over time',
      'Card taps, so you can see which events are worth returning to',
      'For companies, the same picture aggregated across teams and members',
    ],
  },
  {
    heading: 'Making the follow-up happen',
    status: 'In development',
    body: 'Almost no introduction turns into a second conversation, and the reason is rarely the meeting. It is the fortnight afterwards. Because Velox already holds your notes, the place and the date, it can draft the message for you and put it in front of you while the conversation is still worth reviving.',
    points: [
      'A follow-up drafted from what you wrote at the time',
      'A prompt while the meeting is still recent enough to reference',
      'Contacts you have flagged kept in a list of their own',
    ],
  },
];

const HowItWorksPage = () => {
  const revealRef = useReveal();

  useDocumentMeta({
    title: 'How it works',
    description:
      'How Velox works: share your profile, capture the other person\u2019s details, keep the context of where you met, and find them again when they matter.',
    path: '/how-it-works',
  });

  return (
    <div ref={revealRef}>
      <section className={styles.pageHero}>
        <div className="vxm-grid-motif" />
        <div className={`vxm-container vxm-container--narrow ${styles.pageHeroInner}`}>
          <p className="vxm-eyebrow">How it works</p>
          <h1 className="vxm-h1">From meeting to next conversation</h1>
          <p className="vxm-lead">
            Four stages. The card is one way to share. The system is
            what happens after.
          </p>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container">
          <FlowDiagram />
        </div>
      </section>

      <section className="vxm-section">
        <div className="vxm-container vxm-container--narrow">
          <div className={sectionStyles.featureGroups}>
            {DETAIL_SECTIONS.map((section) => (
              <div key={section.heading} className="vxm-reveal">
                <ExpandableCard
                  title={section.heading}
                  status={section.status}
                  alwaysOpenOnDesktop
                >
                  <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-5)' }}>
                    {section.body}
                  </p>
                  <ul className={sectionStyles.featureList}>
                    {section.points.map((point) => (
                      <li key={point} className={sectionStyles.featureItem}>
                        <CheckIcon
                          className={sectionStyles.featureCheck}
                          width={18}
                          height={18}
                        />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </ExpandableCard>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTASection
        title="Ready when you are"
        body="Create a profile and start today, or tell us what you want designed and we will come back with a proof and a price."
        source="how_it_works_final"
      />
    </div>
  );
};

export default HowItWorksPage;
